import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { ClipboardList, Plus, Loader2, Check, X, PackageSearch, Upload, ChevronLeft, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const GOLD = "#C9A24B";
const eur = (n) => (n == null ? "—" : `€${Number(n).toFixed(2)}`);
const STATE_COLOR = { ok: "text-emerald-600", expiring: "text-amber-600", expired: "text-red-600", missing: "text-muted-foreground" };
const STATE_ICON = { ok: "🟢", expiring: "🟠", expired: "🔴", missing: "⚪" };

export default function Inventario() {
  const [sessions, setSessions] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadSessions = useCallback(async () => {
    try { const { data } = await api.get("/inventory/sessions"); setSessions(data); } catch { /* */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { loadSessions(); }, [loadSessions]);

  if (active) return <SessionView id={active} onBack={() => { setActive(null); loadSessions(); }} />;

  return (
    <div data-testid="inventory-page">
      <div className="flex items-center justify-between mb-5">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground">Magazzino</p>
          <h1 className="font-dash text-3xl font-semibold flex items-center gap-2"><ClipboardList className="w-7 h-7" style={{ color: GOLD }} /> Inventari</h1>
        </div>
        <NewSessionButton onCreated={(id) => setActive(id)} />
      </div>
      {loading ? <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div> : (
        <Card className="rounded-xl overflow-hidden" data-testid="sessions-table">
          <div className="grid grid-cols-7 gap-2 px-4 py-2 text-xs uppercase tracking-wide text-muted-foreground border-b border-border">
            <span>ID</span><span>Data</span><span>Operatore</span><span>Modalità</span><span>Contati</span><span className="text-right">Delta €</span><span>Stato</span>
          </div>
          {sessions.length === 0 && <div className="p-8 text-center text-muted-foreground">Nessun inventario. Crea il primo (sarà la baseline reale).</div>}
          {sessions.map((s) => (
            <button key={s.id} onClick={() => setActive(s.id)} data-testid={`session-row-${s.session_code}`}
              className="w-full grid grid-cols-7 gap-2 px-4 py-3 text-sm items-center hover:bg-secondary text-left border-b border-border/50">
              <span className="font-medium truncate">{s.session_code}{s.is_baseline && <span className="ml-1 text-[10px] px-1 rounded bg-amber-500/20 text-amber-600">BASELINE</span>}</span>
              <span className="text-muted-foreground">{s.started_at ? new Date(s.started_at).toLocaleDateString("it-IT") : "—"}</span>
              <span className="truncate">{s.started_by || "—"}</span>
              <span className="uppercase text-xs">{s.mode}</span>
              <span>{s.counted_count ?? 0}</span>
              <span className="text-right">{eur(s.totals?.delta_value)}</span>
              <span><StatusBadge status={s.status} /></span>
            </button>
          ))}
        </Card>
      )}
    </div>
  );
}

const StatusBadge = ({ status }) => {
  const map = { draft: ["Bozza", "bg-secondary text-muted-foreground"], in_progress: ["In corso", "bg-blue-500/15 text-blue-600"],
    completed: ["Completato", "bg-emerald-500/15 text-emerald-600"], cancelled: ["Annullato", "bg-red-500/15 text-red-600"] };
  const [lbl, cls] = map[status] || [status, "bg-secondary"];
  return <span className={`text-[11px] px-2 py-0.5 rounded-full ${cls}`}>{lbl}</span>;
};

const NewSessionButton = ({ onCreated }) => {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("blind");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/inventory/sessions", { mode, notes });
      await api.post(`/inventory/sessions/${data.id}/start`);
      await api.post(`/inventory/sessions/${data.id}/populate`);
      toast.success(`Inventario ${data.session_code} avviato`);
      setOpen(false); onCreated(data.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); } finally { setBusy(false); }
  };
  return (
    <>
      <Button className="rounded-full" style={{ backgroundColor: GOLD, color: "#14110E" }} onClick={() => setOpen(true)} data-testid="new-inventory-button"><Plus className="w-4 h-4 mr-1" /> Nuovo inventario</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-[95vw] max-w-sm" data-testid="new-inventory-dialog">
          <DialogHeader><DialogTitle>Nuovo inventario</DialogTitle><DialogDescription className="text-xs">Il primo inventario fisserà la baseline reale del magazzino.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-muted-foreground">Modalità</label>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger data-testid="inventory-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="blind">Cieca (consigliata)</SelectItem>
                  <SelectItem value="guided">Guidata</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Input placeholder="Note (opzionale)" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="inventory-notes-input" />
          </div>
          <DialogFooter><Button onClick={create} disabled={busy} className="rounded-full" data-testid="confirm-new-inventory">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Avvia"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

function SessionView({ id, onBack }) {
  const [sess, setSess] = useState(null);
  const [term, setTerm] = useState("");
  const [filter, setFilter] = useState("all");
  const [summary, setSummary] = useState(null);
  const [closeOpen, setCloseOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/inventory/sessions/${id}`); setSess(data); } catch { /* */ }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const openClose = async () => {
    try { const { data } = await api.get(`/inventory/sessions/${id}/summary`); setSummary(data); setCloseOpen(true); } catch { toast.error("Errore riepilogo"); }
  };
  const doClose = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/inventory/sessions/${id}/close`); toast.success(`Inventario chiuso · ${data.movement_type}`); setCloseOpen(false); onBack(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); } finally { setBusy(false); }
  };

  if (!sess) return <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  const done = sess.status === "completed";
  let lines = sess.lines || [];
  if (term) lines = lines.filter((l) => (l.ingredient_name || "").toLowerCase().includes(term.toLowerCase()) || (l.batch_lotto || "").toLowerCase().includes(term.toLowerCase()));
  if (filter === "pending") lines = lines.filter((l) => l.status === "pending");
  if (filter === "diff") lines = lines.filter((l) => l.status === "diff");

  return (
    <div data-testid="inventory-session">
      <div className="flex items-center gap-2 mb-4">
        <Button variant="ghost" size="icon" className="rounded-full" onClick={onBack} data-testid="back-to-sessions"><ChevronLeft className="w-5 h-5" /></Button>
        <div className="flex-1">
          <h1 className="font-dash text-2xl font-semibold flex items-center gap-2">{sess.session_code} {sess.is_baseline && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-600">BASELINE</span>}</h1>
          <p className="text-xs text-muted-foreground uppercase">{sess.mode} · <StatusBadge status={sess.status} /></p>
        </div>
        {!done && <ImportButton id={id} onDone={load} />}
        {!done && <AddLotButton id={id} onDone={load} />}
        {!done && <Button className="rounded-full" style={{ backgroundColor: "#16a34a", color: "white" }} onClick={openClose} data-testid="open-close-inventory"><Check className="w-4 h-4 mr-1" /> Chiudi</Button>}
      </div>
      <div className="flex gap-2 mb-3">
        <div className="relative flex-1"><PackageSearch className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" /><Input placeholder="Cerca prodotto / lotto" value={term} onChange={(e) => setTerm(e.target.value)} className="pl-9 rounded-full" data-testid="inventory-search" /></div>
        <Select value={filter} onValueChange={setFilter}><SelectTrigger className="w-40" data-testid="inventory-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tutti</SelectItem><SelectItem value="pending">Non contati</SelectItem><SelectItem value="diff">Con differenze</SelectItem></SelectContent></Select>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {lines.map((l) => <LineCard key={l.id} sid={id} line={l} mode={sess.mode} done={done} onDone={load} />)}
      </div>

      <Dialog open={closeOpen} onOpenChange={setCloseOpen}>
        <DialogContent className="w-[95vw] max-w-md" data-testid="close-inventory-dialog">
          <DialogHeader><DialogTitle>Riepilogo inventario</DialogTitle>
            {summary?.is_baseline && <DialogDescription className="text-amber-600 font-semibold text-xs flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> {summary.baseline_notice}</DialogDescription>}
          </DialogHeader>
          {summary && (
            <div className="space-y-1 text-sm">
              {[["Righe", summary.lines], ["Contati", summary.counted], ["Non presenti", summary.not_present], ["Non contati", summary.pending],
                ["Dati mancanti", summary.missing_data], ["Scaduti", summary.expired], ["In scadenza", summary.expiring], ["Costo mancante", summary.cost_missing]].map(([k, v]) => (
                <div key={k} className="flex justify-between"><span className="text-muted-foreground">{k}</span><span>{v}</span></div>
              ))}
              <div className="flex justify-between border-t border-border pt-2 mt-2"><span className="text-muted-foreground">Valore teorico</span><span>{eur(summary.theoretical_value)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Valore fisico</span><span>{eur(summary.physical_value)}</span></div>
              <div className="flex justify-between font-semibold"><span>Delta €</span><span className={summary.delta_value < 0 ? "text-red-600" : "text-emerald-600"}>{eur(summary.delta_value)}</span></div>
            </div>
          )}
          <DialogFooter><Button onClick={doClose} disabled={busy} style={{ backgroundColor: "#16a34a", color: "white" }} className="rounded-full" data-testid="confirm-close-inventory">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (summary?.is_baseline ? "Chiudi e fissa baseline" : "Chiudi e rettifica")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function LineCard({ sid, line, mode, done, onDone }) {
  const [val, setVal] = useState("");
  const [unit, setUnit] = useState(line.base_unit || "pz");
  const [busy, setBusy] = useState(false);
  const blindHidden = mode === "blind" && line.status === "pending";

  const count = async () => {
    if (val === "") return;
    setBusy(true);
    try { await api.post(`/inventory/sessions/${sid}/lines/${line.id}/count`, { counted_raw: [{ value: parseFloat(val), unit }] }); toast.success("Contato"); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); } finally { setBusy(false); }
  };
  const notPresent = async () => {
    setBusy(true);
    try { await api.post(`/inventory/sessions/${sid}/lines/${line.id}/not-present`); onDone(); }
    catch { toast.error("Errore"); } finally { setBusy(false); }
  };

  return (
    <Card className={`p-3 rounded-2xl ${line.status === "diff" ? "ring-1 ring-amber-500/50" : ""}`} data-testid={`line-card-${line.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-medium truncate">{line.ingredient_name}</p>
          <p className="text-xs text-muted-foreground">Lotto {line.batch_lotto || "—"} · {STATE_ICON[line.expiry_state]} <span className={STATE_COLOR[line.expiry_state]}>{line.expiry || "scad. n/d"}</span></p>
          <p className="text-xs text-muted-foreground">Ingresso {line.received_date || "n/d"} · {line.base_unit}</p>
          {line.possible_duplicate && <p className="text-[11px] text-amber-600">⚠ Possibile duplicato storico</p>}
        </div>
        <div className="text-right text-xs shrink-0">
          {!blindHidden && line.theoretical_qty != null && <p className="text-muted-foreground">Teor: {line.theoretical_qty}</p>}
          {line.counted_qty != null && <p className="font-medium">Fis: {line.counted_qty}</p>}
          {line.delta_qty != null && <p className={line.delta_qty < 0 ? "text-red-600" : line.delta_qty > 0 ? "text-emerald-600" : "text-muted-foreground"}>Δ {line.delta_qty}</p>}
        </div>
      </div>
      {!done && (
        <div className="flex items-center gap-2 mt-3">
          <Input type="number" value={val} onChange={(e) => setVal(e.target.value)} placeholder="Qtà fisica" className="h-9" data-testid={`line-qty-${line.id}`} />
          <Input value={unit} onChange={(e) => setUnit(e.target.value)} className="h-9 w-20" data-testid={`line-unit-${line.id}`} />
          <Button size="icon" className="h-9 w-9 rounded-full shrink-0" style={{ backgroundColor: GOLD, color: "#14110E" }} disabled={busy} onClick={count} data-testid={`line-save-${line.id}`}><Check className="w-4 h-4" /></Button>
          <Button size="icon" variant="outline" className="h-9 w-9 rounded-full shrink-0" disabled={busy} onClick={notPresent} title="Non presente" data-testid={`line-absent-${line.id}`}><X className="w-4 h-4" /></Button>
        </div>
      )}
      {line.status !== "pending" && <p className="text-[11px] mt-1 text-muted-foreground">Stato: {line.status}{line.status === "unit_incompatible" ? " (unità da configurare)" : ""}</p>}
    </Card>
  );
}

const AddLotButton = ({ id, onDone }) => {
  const [open, setOpen] = useState(false);
  const [ings, setIngs] = useState([]);
  const [f, setF] = useState({ ingredient_id: "", lotto: "", expiry: "", quantity: "", unit: "", received_date: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open && ings.length === 0) api.get("/fc/ingredients").then(({ data }) => setIngs(data || [])).catch(() => { }); }, [open, ings.length]);
  const save = async () => {
    if (!f.ingredient_id || f.quantity === "") { toast.error("Ingrediente e quantità obbligatori"); return; }
    setBusy(true);
    try { await api.post(`/inventory/sessions/${id}/add-lot`, { ...f, quantity: parseFloat(f.quantity) }); toast.success("Lotto aggiunto"); setOpen(false); setF({ ingredient_id: "", lotto: "", expiry: "", quantity: "", unit: "", received_date: "" }); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); } finally { setBusy(false); }
  };
  return (
    <>
      <Button variant="outline" className="rounded-full" onClick={() => setOpen(true)} data-testid="add-lot-button"><Plus className="w-4 h-4 mr-1" /> Lotto</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-[95vw] max-w-sm" data-testid="add-lot-dialog">
          <DialogHeader><DialogTitle>Aggiungi prodotto/lotto</DialogTitle><DialogDescription className="text-xs">Nessun dato viene inventato. Lotto/scadenza mancanti = avviso.</DialogDescription></DialogHeader>
          <div className="space-y-2">
            <Select value={f.ingredient_id} onValueChange={(v) => setF({ ...f, ingredient_id: v })}>
              <SelectTrigger data-testid="add-lot-ingredient"><SelectValue placeholder="Ingrediente Master" /></SelectTrigger>
              <SelectContent className="max-h-64">{ings.map((i) => <SelectItem key={i.id} value={i.id}>{i.canonical_name || i.name}</SelectItem>)}</SelectContent>
            </Select>
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="Lotto" value={f.lotto} onChange={(e) => setF({ ...f, lotto: e.target.value })} data-testid="add-lot-lotto" />
              <Input placeholder="Scadenza YYYY-MM-DD" value={f.expiry} onChange={(e) => setF({ ...f, expiry: e.target.value })} data-testid="add-lot-expiry" />
              <Input placeholder="Quantità" type="number" value={f.quantity} onChange={(e) => setF({ ...f, quantity: e.target.value })} data-testid="add-lot-qty" />
              <Input placeholder="Unità" value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })} data-testid="add-lot-unit" />
            </div>
          </div>
          <DialogFooter><Button onClick={save} disabled={busy} className="rounded-full" data-testid="confirm-add-lot">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Aggiungi"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

const ImportButton = ({ id, onDone }) => {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const doPreview = async (fl) => {
    setFile(fl); setBusy(true);
    const fd = new FormData(); fd.append("file", fl);
    try { const { data } = await api.post("/inventory/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } }); setPreview(data); }
    catch (e) { toast.error(e.response?.data?.detail || "File non valido"); } finally { setBusy(false); }
  };
  const commit = async () => {
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post(`/inventory/sessions/${id}/import`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success(`Importati ${data.imported} righe`); setOpen(false); setPreview(null); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); } finally { setBusy(false); }
  };
  return (
    <>
      <Button variant="outline" className="rounded-full" onClick={() => setOpen(true)} data-testid="import-button"><Upload className="w-4 h-4 mr-1" /> Import</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-[95vw] max-w-md" data-testid="import-dialog">
          <DialogHeader><DialogTitle>Importa inventario (CSV/XLSX)</DialogTitle><DialogDescription className="text-xs">Colonne: prodotto, EAN, codice fornitore, lotto, scadenza, quantità, unità. Nessuna associazione dubbia automatica.</DialogDescription></DialogHeader>
          <input type="file" accept=".csv,.xlsx,.xls" onChange={(e) => e.target.files[0] && doPreview(e.target.files[0])} data-testid="import-file-input" className="text-sm" />
          {busy && <Loader2 className="w-5 h-5 animate-spin mx-auto" />}
          {preview && (
            <div className="text-sm space-y-1" data-testid="import-preview">
              <div className="flex justify-between"><span className="text-emerald-600">Riconosciuti</span><span>{preview.counts.recognized}</span></div>
              <div className="flex justify-between"><span className="text-red-600">Non riconosciuti</span><span>{preview.counts.unrecognized}</span></div>
              <div className="flex justify-between"><span className="text-amber-600">Ambigui</span><span>{preview.counts.ambiguous}</span></div>
              <div className="flex justify-between"><span className="text-amber-600">Unità incompatibili</span><span>{preview.counts.unit_incompatible}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Lotti sconosciuti</span><span>{preview.counts.unknown_lot}</span></div>
            </div>
          )}
          <DialogFooter><Button onClick={commit} disabled={busy || !preview || preview.counts.recognized === 0} className="rounded-full" data-testid="confirm-import">Importa riconosciuti</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
